import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, LogOut, Search, Trash2, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { http, API } from "../lib/api";
import { Logo } from "../components/site/Header";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";

const STATUSES = [["new", "New"], ["contacted", "Contacted"], ["in_progress", "In progress"], ["archived", "Archived"]];
const LABEL = Object.fromEntries(STATUSES);
const TONE = { new: "bg-[#E8F1FD] text-[#2878E5]", contacted: "bg-amber-50 text-amber-700", in_progress: "bg-emerald-50 text-emerald-700", archived: "bg-slate-100 text-slate-600" };
const fmt = (d) => new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const Badge = ({ s }) => <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${TONE[s] ?? TONE.archived}`}>{LABEL[s] ?? s}</span>;

const Detail = ({ e, onStatus, onDelete }) => (
  <div className="mt-6 space-y-5 text-sm">
    {[["Company", e.company], ["Email", e.email], ["Phone", e.phone || "-"], ["Country", e.country], ["Partnership", e.partnership_type], ["Service", e.service || "-"], ["Form", e.source], ["Received", fmt(e.created_at)]].map(([k, v]) => (
      <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-3"><span className="text-slate-500">{k}</span><span className="text-right font-medium text-[#102B50] break-all">{v}</span></div>
    ))}
    <div><p className="text-slate-500">Message</p><p data-testid="enquiry-detail-message" className="mt-2 whitespace-pre-wrap rounded-xl bg-[#F4F7FB] p-4 text-[#2D3F58]">{e.message}</p></div>
    <div>
      <p className="mb-2 text-slate-500">Status</p>
      <div className="flex flex-wrap gap-2">
        {STATUSES.map(([k, l]) => (
          <button key={k} data-testid={`detail-status-${k}`} onClick={() => onStatus(e, k)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${e.status === k ? "border-[#2878E5] bg-[#2878E5] text-white" : "border-slate-200 text-[#102B50] hover:border-[#2878E5]"}`}>{l}</button>
        ))}
      </div>
    </div>
    <div className="flex gap-3 pt-2">
      <a href={`mailto:${e.email}`} className="rounded-full bg-[#102B50] px-4 py-2 text-xs font-semibold text-white">Reply by email</a>
      <button data-testid="detail-delete-btn" onClick={() => onDelete(e)} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" />Delete</button>
    </div>
  </div>
);

export default function AdminDashboard({ user, onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get("/admin/enquiries");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch { toast.error("Could not load enquiries"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => Object.fromEntries(STATUSES.map(([k]) => [k, items.filter((i) => i.status === k).length])), [items]);
  const shown = useMemo(() => items.filter((i) => (!status || i.status === status) && (!q || `${i.full_name} ${i.company} ${i.email} ${i.country}`.toLowerCase().includes(q.toLowerCase()))), [items, status, q]);

  const setItemStatus = async (e, s) => {
    await http.patch(`/admin/enquiries/${e.enquiry_id}`, { status: s });
    setItems((xs) => xs.map((x) => (x.enquiry_id === e.enquiry_id ? { ...x, status: s } : x)));
    setOpen((o) => (o ? { ...o, status: s } : o));
    toast.success(`Marked as ${LABEL[s]}`);
  };
  const del = async (e) => {
    if (!window.confirm(`Delete enquiry from ${e.full_name}?`)) return;
    await http.delete(`/admin/enquiries/${e.enquiry_id}`);
    setItems((xs) => xs.filter((x) => x.enquiry_id !== e.enquiry_id));
    setOpen(null);
    toast.success("Enquiry deleted");
  };
  const logout = async () => { await http.post("/auth/logout").catch(() => {}); onLogout(); };

  return (
    <div className="min-h-screen bg-[#F4F7FB]" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
          <Logo className="h-8" />
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-500 sm:block">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-sm text-[#102B50] hover:border-[#102B50]"><LogOut className="h-4 w-4" />Sign out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-10">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h1 className="font-display text-3xl font-bold text-[#102B50]">Enquiries</h1>
            <p className="mt-1 text-sm text-slate-500">All submissions from the contact, advertiser and publisher forms.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="admin-refresh-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-[#102B50]"><RefreshCw className="h-4 w-4" />Refresh</button>
            <a href={`${API}/admin/enquiries.csv`} data-testid="admin-export-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#2878E5] px-4 py-2 text-sm font-semibold text-white"><Download className="h-4 w-4" />Export CSV</a>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-5">
          {[["", "All", items.length], ...STATUSES.map(([k, l]) => [k, l, counts[k]])].map(([k, l, n]) => (
            <button key={l} onClick={() => setStatus(k)} data-testid={`admin-filter-${k || "all"}`} className={`rounded-2xl border p-4 text-left transition-colors ${status === k ? "border-[#2878E5] bg-white shadow-sm" : "border-slate-200 bg-white/60 hover:bg-white"}`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</p>
              <p className="mt-1 font-display text-2xl font-bold text-[#102B50]">{n}</p>
            </button>
          ))}
        </div>
        <div className="relative mt-6">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" placeholder="Search by name, company, email or country" className="field !pl-11" />
        </div>
        <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-[#F8FAFD] text-xs uppercase tracking-wider text-slate-500">
              <tr>{["Received", "Name", "Company", "Type", "Country", "Service", "Status"].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {shown.map((e) => (
                <tr key={e.enquiry_id} data-testid={`enquiry-row-${e.enquiry_id}`} onClick={() => setOpen(e)} className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-[#F4F7FB]">
                  <td className="whitespace-nowrap px-5 py-4 text-slate-500">{fmt(e.created_at)}</td>
                  <td className="px-5 py-4 font-semibold text-[#102B50]">{e.full_name}<span className="block text-xs font-normal text-slate-500">{e.email}</span></td>
                  <td className="px-5 py-4">{e.company}</td>
                  <td className="px-5 py-4">{e.partnership_type}</td>
                  <td className="px-5 py-4">{e.country}</td>
                  <td className="px-5 py-4">{e.service || "-"}</td>
                  <td className="px-5 py-4"><Badge s={e.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && shown.length === 0 && (
            <div data-testid="admin-empty" className="flex flex-col items-center py-16 text-slate-500"><Inbox className="h-8 w-8" /><p className="mt-3 text-sm">No enquiries found.</p></div>
          )}
          {loading && <p className="py-16 text-center text-sm text-slate-500">Loading…</p>}
        </div>
      </main>
      <Sheet open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="enquiry-detail">
          {open && (
            <>
              <SheetHeader><SheetTitle className="font-display text-xl text-[#102B50]">{open.full_name}</SheetTitle></SheetHeader>
              <Detail e={open} onStatus={setItemStatus} onDelete={del} />
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
